import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';
import { fetchLearningData, analyzeOverallStats, analyzeStudentData, analyzeActivityDetail } from '../data/구글시트';
import Header from './Header';

const AILearningDashboard = () => {
  const [sheetUrl, setSheetUrl] = useState('');
  const [rawData, setRawData] = useState([]);
  const [overallStats, setOverallStats] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentStats, setStudentStats] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityDetail, setActivityDetail] = useState(null);
  const [currentView, setCurrentView] = useState('overview'); // 'overview', 'student', 'activity'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // localStorage에서 구글 시트 URL 로드
  useEffect(() => {
    const loadSavedSettings = () => {
      try {
        const savedSettings = localStorage.getItem('user_default_settings');
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          if (parsedSettings.defaultSheetUrl) {
            setSheetUrl(parsedSettings.defaultSheetUrl);
            console.log('✅ 저장된 구글 시트 URL 로드됨:', parsedSettings.defaultSheetUrl);
          }
        }
      } catch (error) {
        console.error('❌ 저장된 설정 로드 실패:', error);
      }
    };

    loadSavedSettings();
  }, []);

  // 학습 데이터 가져오기 및 분석
  const handleAnalyze = async () => {
    if (!sheetUrl.trim()) {
      setError('구글 시트 URL을 입력해주세요.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log('📊 학습 데이터 분석 시작');
      
      // 구글 시트에서 데이터 가져오기
      const data = await fetchLearningData(sheetUrl);
      setRawData(data);

      // 전체 현황 분석
      const stats = analyzeOverallStats(data);
      setOverallStats(stats);

      console.log('✅ 데이터 분석 완료:', data.length, '개 항목');
      setCurrentView('overview');

    } catch (error) {
      console.error('❌ 데이터 분석 실패:', error);
      setError(`데이터 분석 실패: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // 학생 선택 시 학생별 분석
  const handleStudentSelect = (studentName) => {
    if (!studentName || !rawData.length) return;

    const studentData = analyzeStudentData(rawData, studentName);
    setSelectedStudent(studentName);
    setStudentStats(studentData);
    setCurrentView('student');
  };

  // 활동 선택 시 상세 분석
  const handleActivitySelect = (activity) => {
    if (!activity) return;

    const detail = analyzeActivityDetail(activity);
    setSelectedActivity(activity);
    setActivityDetail(detail);
    setCurrentView('activity');
  };

  // 시간 포맷 함수
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}초`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}분 ${remainingSeconds}초` : `${minutes}분`;
  };

  // 색상 배열 (차트용)
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#d084d0'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header 컴포넌트 */}
      <Header />
      
      {/* 페이지 제목 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <h1 className="text-2xl font-bold text-gray-900">📊 AI 학습 대시보드</h1>
            <p className="text-sm text-gray-600 mt-1">학습 데이터를 분석하여 AI 기반 피드백과 인사이트를 제공합니다</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 구글 시트 URL 입력 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">데이터 소스</h2>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                구글 시트 URL
              </label>
              <input
                type="url"
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                placeholder="https://docs.google.com/spreadsheets/d/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={isLoading}
              />
            </div>
            <div className="flex-shrink-0 flex items-end">
              <button
                onClick={handleAnalyze}
                disabled={isLoading || !sheetUrl.trim()}
                className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                {isLoading ? '분석 중...' : '📊 분석하기'}
              </button>
            </div>
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* 네비게이션 */}
        {overallStats && (
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCurrentView('overview')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  currentView === 'overview' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                📊 전체 현황
              </button>
              {selectedStudent && (
                <button
                  onClick={() => setCurrentView('student')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    currentView === 'student' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🧑‍🎓 {selectedStudent} 분석
                </button>
              )}
              {selectedActivity && (
                <button
                  onClick={() => setCurrentView('activity')}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    currentView === 'activity' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🔬 활동 상세
                </button>
              )}
            </div>
          </div>
        )}

        {/* 1단계: 전체 현황 대시보드 */}
        {currentView === 'overview' && overallStats && (
          <div className="space-y-6">
            {/* 핵심 지표 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                      <span className="text-blue-600 text-lg">👥</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-medium text-gray-500">총 학습 참여 학생</h3>
                    <p className="text-2xl font-bold text-gray-900">{overallStats.totalStudents}명</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                      <span className="text-green-600 text-lg">⏰</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-medium text-gray-500">총 학습 시간</h3>
                    <p className="text-2xl font-bold text-gray-900">{formatTime(overallStats.totalLearningTime)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                      <span className="text-yellow-600 text-lg">🎯</span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-sm font-medium text-gray-500">평균 정답률</h3>
                    <p className="text-2xl font-bold text-gray-900">{overallStats.averageAccuracy}%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* 기능별 사용량 차트 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 기능별 사용량</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart 
                  data={overallStats.functionStats}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    fontSize={12}
                  />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* 기능별 평균 정답률 차트 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🎯 기능별 평균 정답률</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart 
                  data={overallStats.functionStats}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={100}
                    interval={0}
                    fontSize={12}
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip formatter={(value) => [`${value}%`, '평균 정답률']} />
                  <Bar dataKey="averageAccuracy" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* 일별 학습 추이 */}
            {overallStats.dailyStats.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 일별 학습 참여 추이</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={overallStats.dailyStats}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" name="총 활동 수" />
                    <Line type="monotone" dataKey="uniqueStudents" stroke="#82ca9d" name="참여 학생 수" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 학생 목록 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🧑‍🎓 학생별 분석하기</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {Array.from(new Set(rawData.map(entry => entry['학생이름']))).map(studentName => (
                  <button
                    key={studentName}
                    onClick={() => handleStudentSelect(studentName)}
                    className="p-3 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm font-medium text-blue-800 transition-colors"
                  >
                    {studentName}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* 2단계: 학생별 대시보드 */}
        {currentView === 'student' && studentStats && (
          <div className="space-y-6">
            {/* 학생 프로필 요약 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🧑‍🎓 {studentStats.studentName} 학습 프로필</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{studentStats.totalSessions}회</p>
                  <p className="text-sm text-gray-500">총 접속 횟수</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{formatTime(studentStats.totalLearningTime)}</p>
                  <p className="text-sm text-gray-500">총 학습 시간</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{studentStats.averageAccuracy}%</p>
                  <p className="text-sm text-gray-500">평균 정답률</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-purple-600">{studentStats.mostUsedFunction}</p>
                  <p className="text-sm text-gray-500">주요 학습 기능</p>
                </div>
              </div>
            </div>

            {/* 학습 시간 추이 */}
            {studentStats.learningTrend.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 학습 시간 추이</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={studentStats.learningTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name, props) => {
                        if (props.dataKey === 'learningTime') {
                          return [formatTime(value), '학습 시간'];
                        } else if (props.dataKey === 'accuracy') {
                          return [`${Math.round(value)}%`, '정답률'];
                        }
                        return [value, name];
                      }}
                      labelFormatter={(date) => `날짜: ${date}`}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="learningTime" stroke="#8884d8" name="학습 시간 (초)" />
                    <Line type="monotone" dataKey="accuracy" stroke="#82ca9d" name="정답률 (%)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* 기능별 성취도 */}
            {studentStats.functionPerformance.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🎯 기능별 성취도</h3>
                <div className="mb-4">
                  <p className="text-sm text-gray-600">
                    각 학습 기능별 평균 정답률을 비교하여 {studentStats.studentName} 학생의 강점과 약점을 파악할 수 있습니다.
                  </p>
                </div>
                {studentStats.functionPerformance.length > 0 ? (
                  <>
                    {console.log('🔍 기능별 성취도 데이터:', studentStats.functionPerformance)}
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={studentStats.functionPerformance}
                        margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="function" 
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={0}
                        />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="averageAccuracy" fill="#4F46E5" />
                      </BarChart>
                    </ResponsiveContainer>
                  </>
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-500">
                    기능별 성취도 데이터가 없습니다.
                  </div>
                )}
                
                {/* 성취도 분석 */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {studentStats.functionPerformance.map((item, index) => {
                    const getPerformanceColor = (accuracy) => {
                      if (accuracy >= 80) return 'text-green-600 bg-green-50';
                      if (accuracy >= 60) return 'text-yellow-600 bg-yellow-50';
                      return 'text-red-600 bg-red-50';
                    };
                    
                    const getPerformanceLabel = (accuracy) => {
                      if (accuracy >= 80) return '우수';
                      if (accuracy >= 60) return '보통';
                      return '개선필요';
                    };
                    
                    return (
                      <div key={index} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-700">{item.function}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${getPerformanceColor(item.averageAccuracy)}`}>
                            {getPerformanceLabel(item.averageAccuracy)}
                          </span>
                        </div>
                        <div className="mt-1 flex justify-between items-end">
                          <span className="text-lg font-bold text-gray-900">{item.averageAccuracy}%</span>
                          <span className="text-xs text-gray-500">{item.sessionCount || 0}회 학습</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 학습 활동 이력 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 학습 활동 이력</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">기능명</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">접속시간</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">학습시간</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">정답률</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">힌트사용</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상세</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {studentStats.activities.map((activity, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {activity['기능명']}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {activity['접속시간']}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {activity['학습시간']}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {activity.parsedScore ? `${activity.parsedScore.accuracy}%` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {activity['힌트사용여부'] === 'TRUE' ? '✅' : '❌'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <button
                            onClick={() => handleActivitySelect(activity)}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            상세 보기
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 3단계: 활동 상세 분석 */}
        {currentView === 'activity' && activityDetail && (
          <div className="space-y-6">
            {/* 활동 기본 정보 */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🔬 활동 상세 분석</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">활동 유형</p>
                  <p className="font-medium">{activityDetail.activityType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">학생</p>
                  <p className="font-medium">{activityDetail.studentName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">학습 시간</p>
                  <p className="font-medium">{activityDetail.learningTime}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">힌트 사용</p>
                  <p className="font-medium">{activityDetail.hintUsed ? '사용함' : '사용안함'}</p>
                </div>
              </div>
            </div>

            {/* 퀴즈 상세 분석 */}
            {(activityDetail.analysisType === 'quiz' && activityDetail.detailedAnswers) && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📝 문항별 상세 분석</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">문항</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">제시어</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">학생 답안</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">정답</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">결과</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">힌트</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {activityDetail.detailedAnswers.map((answer, index) => (
                        <tr key={index} className={answer.isCorrect ? 'bg-green-50' : 'bg-red-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {index + 1}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {answer.question}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {answer.userAnswer || answer.selectedAnswer}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {answer.correctAnswer}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {answer.isCorrect ? 
                              <span className="text-green-600 font-medium">정답</span> : 
                              <span className="text-red-600 font-medium">오답</span>
                            }
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {answer.hintUsed ? '✅' : '❌'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* 플래시카드 상세 분석 */}
            {activityDetail.analysisType === 'flashcard' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📚 플래시카드 학습 분석</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{activityDetail.totalWords}</p>
                    <p className="text-sm text-gray-500">총 단어 수</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{activityDetail.knownWordsCount}</p>
                    <p className="text-sm text-gray-500">알고 있는 단어</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{activityDetail.unknownWordsCount}</p>
                    <p className="text-sm text-gray-500">모르는 단어</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-600">{activityDetail.finalAccuracy}%</p>
                    <p className="text-sm text-gray-500">정답률</p>
                  </div>
                </div>
                {activityDetail.wordList && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">학습한 단어 목록:</p>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{activityDetail.wordList}</p>
                  </div>
                )}
              </div>
            )}

            {/* 독해 문제 상세 분석 */}
            {activityDetail.analysisType === 'reading' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📖 독해 문제 분석</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{activityDetail.totalQuestions}</p>
                    <p className="text-sm text-gray-500">총 문항 수</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{activityDetail.totalScore}</p>
                    <p className="text-sm text-gray-500">획득 점수</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">{activityDetail.maxScore}</p>
                    <p className="text-sm text-gray-500">만점</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-600">{activityDetail.percentage}%</p>
                    <p className="text-sm text-gray-500">정답률</p>
                  </div>
                </div>
                {activityDetail.passagePreview && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">지문 미리보기:</p>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{activityDetail.passagePreview}</p>
                  </div>
                )}
              </div>
            )}

            {/* 쉐도잉 학습 상세 분석 */}
            {activityDetail.analysisType === 'shadowing' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🎯 쉐도잉 학습 분석</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{activityDetail.totalSentences}</p>
                    <p className="text-sm text-gray-500">총 문장 수</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{activityDetail.passedSentences}</p>
                    <p className="text-sm text-gray-500">통과 문장</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{activityDetail.failedSentences}</p>
                    <p className="text-sm text-gray-500">실패 문장</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-600">{activityDetail.averageAccuracy}%</p>
                    <p className="text-sm text-gray-500">평균 정확도</p>
                  </div>
                </div>

                {/* 문장별 연습 결과 */}
                {activityDetail.practicedSentences && activityDetail.practicedSentences.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-800 mb-4">📝 문장별 연습 결과</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">문장</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">원본 문장</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">사용자 발음</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">정확도</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">결과</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {activityDetail.practicedSentences.map((sentence, index) => (
                            <tr key={index} className={sentence.passed ? 'bg-green-50' : 'bg-red-50'}>
                              <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                {sentence.number}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                                <div className="truncate" title={sentence.original}>
                                  {sentence.original}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-700 max-w-xs">
                                <div className="truncate" title={sentence.userSpeech}>
                                  {sentence.userSpeech || '인식 실패'}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm font-medium">
                                <span className={sentence.accuracy >= 70 ? 'text-green-600' : 'text-red-600'}>
                                  {sentence.accuracy}%
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm">
                                {sentence.passed ? 
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    통과
                                  </span> : 
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    실패
                                  </span>
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 발음 정확도 분포 차트 */}
                {activityDetail.sentenceScores && activityDetail.sentenceScores.length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-md font-semibold text-gray-800 mb-4">📊 발음 정확도 분포</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={activityDetail.sentenceScores.map((score, index) => ({ 
                        sentence: `문장 ${index + 1}`, 
                        accuracy: Math.round(score) 
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="sentence" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip formatter={(value) => [`${value}%`, '정확도']} />
                        <Bar dataKey="accuracy" fill="#4F46E5" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AILearningDashboard;